/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ErrorModel } from '@/shared/api/BaseAPI.model'
import { useTranslation } from 'react-i18next'
import ToppingsAPI from '../api/ToppingsAPI'
import {
  QUERY,
  toppingPayloadModel,
  toppingPutPayloadModel
} from '../api/ToppingsAPI.model'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'

export function useToppingsQuery(toppingId?: string) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()

  const { data: toppingsList, isLoading: toppingsListLoading } = useQuery({
    queryKey: [QUERY.TOPPINGS],
    queryFn: ToppingsAPI.getToppings
  })

  const topping = useQuery({
    queryKey: [QUERY.TOPPINGS, toppingId],
    queryFn: () => ToppingsAPI.getTopping(toppingId!),
    enabled: !!toppingId
  })

  const { mutate: putTopping } = useMutation<
    toppingPutPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (toppingPayload: toppingPutPayloadModel) =>
      ToppingsAPI.putTopping(
        toppingPayload.toppingPayload,
        toppingPayload.toppingId
      ),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.PUT_TOPPING_SUCCESS.TITLE'),
        t('ALERTS.PUT_TOPPING_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.TOPPINGS] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.PUT_TOPPING_ERROR.TITLE'),
        t('ALERTS.PUT_TOPPING_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: postTopping } = useMutation<
    toppingPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (toppingPayload: toppingPayloadModel) =>
      ToppingsAPI.postTopping(toppingPayload),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.POST_TOPPING_SUCCESS.TITLE'),
        t('ALERTS.POST_TOPPING_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.TOPPINGS] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_TOPPING_ERROR.TITLE'),
        t('ALERTS.POST_TOPPING_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteToppings } = useMutation<void, ErrorModel, any>({
    mutationFn: (toppingIds: string[]) =>
      ToppingsAPI.deleteToppings(toppingIds),
    onSuccess: () => {
      showSnackbar(
        t('ALERTS.DELETE_TOPPINGS_SUCCESS.TITLE'),
        t('ALERTS.DELETE_TOPPINGS_SUCCESS.DESCRIPTION'),
        'success'
      )
      queryClient.invalidateQueries({ queryKey: [QUERY.TOPPINGS] })
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_TOPPINGS_ERROR.TITLE'),
        t('ALERTS.DELETE_TOPPINGS_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  return {
    toppingsList,
    toppingsListLoading,
    topping,
    putTopping,
    postTopping,
    deleteToppings
  }
}
